import axios, { AxiosError, AxiosResponse } from "axios";
import i18n from "i18next";
import Cookies from "js-cookie";
import { refreshToken } from "./authApi";

// Единый инстанс axios, который будем использовать во всех REST-обёртках
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_URL_MAIN_SERVICE,
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

// ---------------------- REQUEST INTERCEPTOR ----------------------
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = Cookies.get("access_token");

    // Подставляем Bearer из куки во все запросы
    if (accessToken) {
      config.headers = config.headers || {};
      (config.headers as any).Authorization = `Bearer ${accessToken}`;
    }

    // Устанавливаем язык
    config.headers = config.headers || {};
    (config.headers as any)["Accept-Language"] = i18n.language?.includes("ru")
      ? "ru-RU"
      : "kk-KZ";

    return config;
  },
  (error) => Promise.reject(error),
);

// ---------------------- RESPONSE INTERCEPTOR (REFRESH TOKEN) ----------------------
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<any>) => {
    const originalRequest: any = error.config;

    // Если нет ответа или нет статуса — просто пробрасываем ошибку
    if (!error.response) {
      return Promise.reject(error);
    }

    const status = error.response.status;

    // Обрабатываем только 401 (просроченный / невалидный access-token)
    if (status === 401 && !originalRequest._retry) {
      const refresh_token = Cookies.get("refresh_token");

      // Если refresh токена нет — просто выкидываем ошибку (дальше может сработать редирект на логин)
      if (!refresh_token) {
        return Promise.reject(error);
      }

      // Если уже идёт процесс обновления — ставим запрос в очередь
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token: any) => {
            // После получения нового токена повторяем оригинальный запрос
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await refreshToken(refresh_token);

        // Бэкенд может вернуть токены в разных полях — подстрахуемся
        const newAccessToken =
          res?.access_token || res?.accessToken || res?.data?.access_token;
        const newRefreshToken =
          res?.refresh_token || res?.refreshToken || res?.data?.refresh_token;

        // Очищаем старые и сохраняем новые токены
        Cookies.remove("access_token");
        Cookies.remove("refresh_token");
        Cookies.remove("token_expiry");

        if (newAccessToken) {
          Cookies.set("access_token", newAccessToken);
          // Обновляем дефолтный хедер для всех будущих запросов
          apiClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        }
        if (newRefreshToken) {
          Cookies.set("refresh_token", newRefreshToken);
        }

        processQueue(null, newAccessToken || null);
        isRefreshing = false;

        // Повторяем оригинальный запрос с новым токеном
        originalRequest.headers = originalRequest.headers || {};
        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError: any) {
        console.error("Ошибка при обновлении токена:", refreshError);

        processQueue(refreshError, null);
        isRefreshing = false;

        // Если refresh тоже не удался — чистим куки и пробрасываем ошибку наружу
        Cookies.remove("access_token");
        Cookies.remove("refresh_token");
        Cookies.remove("token_expiry");

        return Promise.reject(refreshError);
      }
    }

    // Для всех остальных статусов просто пробрасываем ошибку
    return Promise.reject(error);
  },
);

export default apiClient;
